import type { Dispatch, SetStateAction } from 'react'

import { Button } from 'components/Button'
import { Icon } from 'components/Icon'
import type { TTag } from 'types'
import {
  createEmptyComboCondition,
  createEmptyComboRule,
  MAX_COMBO_CONDITIONS,
  MAX_COMBO_RULES,
  type TSurveyBuilderComboCondition,
  type TSurveyBuilderComboRule,
  type TSurveyBuilderQuestion,
} from './builder'
import { SectionCard, selectClassName, SingleTagPicker } from './fields'

type Props = {
  comboRules: TSurveyBuilderComboRule[]
  setComboRules: Dispatch<SetStateAction<TSurveyBuilderComboRule[]>>
  questions: TSurveyBuilderQuestion[]
  tags: TTag[]
}

const getOtherSelectedChoiceIds = (
  rule: TSurveyBuilderComboRule,
  conditionClientId: string,
  questionClientId: string,
) =>
  new Set(
    rule.conditions
      .filter(
        (condition) =>
          condition.clientId !== conditionClientId &&
          condition.questionClientId === questionClientId &&
          condition.choiceClientId,
      )
      .map((condition) => condition.choiceClientId),
  )

const isQuestionUnavailable = (
  rule: TSurveyBuilderComboRule,
  condition: TSurveyBuilderComboCondition,
  question: TSurveyBuilderQuestion,
) => {
  if (condition.questionClientId === question.clientId) return false

  const selectedChoiceIds = getOtherSelectedChoiceIds(
    rule,
    condition.clientId,
    question.clientId,
  )
  if (question.type === 'SINGLE') return selectedChoiceIds.size > 0
  return (
    question.type === 'MULTI' &&
    question.maxSelections !== undefined &&
    selectedChoiceIds.size >= question.maxSelections
  )
}

const isChoiceUnavailable = (
  rule: TSurveyBuilderComboRule,
  condition: TSurveyBuilderComboCondition,
  question: TSurveyBuilderQuestion,
  choiceClientId: string,
) => {
  if (
    condition.questionClientId === question.clientId &&
    condition.choiceClientId === choiceClientId
  ) {
    return false
  }

  const selectedChoiceIds = getOtherSelectedChoiceIds(
    rule,
    condition.clientId,
    question.clientId,
  )
  if (selectedChoiceIds.has(choiceClientId)) return true
  if (question.type === 'SINGLE') return selectedChoiceIds.size > 0
  return (
    question.type === 'MULTI' &&
    question.maxSelections !== undefined &&
    selectedChoiceIds.size >= question.maxSelections
  )
}

const getRuleErrors = (
  rule: TSurveyBuilderComboRule,
  questions: TSurveyBuilderQuestion[],
) => {
  const errors: string[] = []
  if (rule.conditions.length > MAX_COMBO_CONDITIONS) {
    errors.push(
      `条件は最大${MAX_COMBO_CONDITIONS}件です。超過分を削除してください。`,
    )
  }

  for (const question of questions) {
    const selectedChoiceIds = new Set(
      rule.conditions
        .filter(
          (condition) =>
            condition.questionClientId === question.clientId &&
            condition.choiceClientId,
        )
        .map((condition) => condition.choiceClientId),
    )
    const questionName = question.title || '名称未入力の設問'
    if (question.type === 'SINGLE' && selectedChoiceIds.size > 1) {
      errors.push(
        `単一選択の設問「${questionName}」に複数の選択肢が指定されています。条件を減らしてください。`,
      )
    }
    if (
      question.type === 'MULTI' &&
      question.maxSelections !== undefined &&
      selectedChoiceIds.size > question.maxSelections
    ) {
      errors.push(
        `複数選択の設問「${questionName}」は最大選択数${question.maxSelections}を超えています。条件を減らしてください。`,
      )
    }
  }
  return errors
}

export function ComboRuleEditor({
  comboRules,
  setComboRules,
  questions,
  tags,
}: Props) {
  const choiceQuestions = questions.filter(
    (question) =>
      (question.type === 'SINGLE' || question.type === 'MULTI') &&
      (question.choices?.length ?? 0) > 0,
  )

  const updateRule = (
    ruleClientId: string,
    update: (rule: TSurveyBuilderComboRule) => TSurveyBuilderComboRule,
  ) => {
    setComboRules((current) =>
      current.map((rule) =>
        rule.clientId === ruleClientId ? update(rule) : rule,
      ),
    )
  }

  return (
    <SectionCard
      title="組み合わせタグ"
      description="指定した回答をすべて満たした友だちにタグを付けます。"
      headingId="combo-rules-heading"
      badge={
        comboRules.length > 0 ? (
          <span className="rounded-full bg-surface px-3 py-1 text-xs font-semibold text-muted">
            {comboRules.length} / {MAX_COMBO_RULES}件
          </span>
        ) : undefined
      }
    >
      {comboRules.map((rule, ruleIndex) => (
        <div
          key={rule.clientId}
          className="space-y-4 rounded-2xl border border-border/60 bg-white p-5 shadow-sm"
        >
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-ink/80 text-xs font-bold text-white">
                {ruleIndex + 1}
              </span>
              <h3 className="text-sm font-semibold text-ink">
                ルール {ruleIndex + 1}
              </h3>
            </div>
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="text-danger"
              aria-label={`組み合わせタグ ${ruleIndex + 1}を削除`}
              onClick={() =>
                setComboRules((current) =>
                  current.filter(
                    (currentRule) => currentRule.clientId !== rule.clientId,
                  ),
                )
              }
            >
              ルールを削除
            </Button>
          </div>

          {getRuleErrors(rule, choiceQuestions).map((error, errorIndex) => (
            <p
              key={`${rule.clientId}-error-${errorIndex}`}
              className="rounded-xl border border-danger-border bg-danger-soft px-4 py-3 text-sm text-danger"
              role="alert"
            >
              {error}
            </p>
          ))}

          <div className="grid gap-2 rounded-xl border border-border/60 bg-surface/60 p-4">
            <span className="text-xs font-semibold text-ink">
              条件（すべて満たしたとき）
            </span>
            <div className="grid gap-2">
              {rule.conditions.map((condition, conditionIndex) => {
                const selectedQuestion = choiceQuestions.find(
                  (question) =>
                    question.clientId === condition.questionClientId,
                )
                return (
                  <div
                    key={condition.clientId}
                    className="grid items-end gap-2 md:grid-cols-[1fr_1fr_auto]"
                  >
                    <label className="grid gap-1.5 text-xs font-semibold text-ink">
                      <span>設問</span>
                      <select
                        className={selectClassName}
                        aria-label={`組み合わせタグ ${ruleIndex + 1} 条件 ${conditionIndex + 1}の設問`}
                        value={condition.questionClientId}
                        onChange={(event) => {
                          const questionClientId = event.target.value
                          updateRule(rule.clientId, (current) => {
                            const currentCondition = current.conditions.find(
                              (candidate) =>
                                candidate.clientId === condition.clientId,
                            )
                            const nextQuestion = choiceQuestions.find(
                              (question) =>
                                question.clientId === questionClientId,
                            )
                            if (
                              !currentCondition ||
                              (nextQuestion &&
                                isQuestionUnavailable(
                                  current,
                                  currentCondition,
                                  nextQuestion,
                                ))
                            ) {
                              return current
                            }
                            return {
                              ...current,
                              conditions: current.conditions.map((candidate) =>
                                candidate.clientId === condition.clientId
                                  ? {
                                      ...candidate,
                                      questionClientId,
                                      choiceClientId: '',
                                    }
                                  : candidate,
                              ),
                            }
                          })
                        }}
                      >
                        <option value="">設問を選択</option>
                        {choiceQuestions.map((question) => (
                          <option
                            key={question.clientId}
                            value={question.clientId}
                            disabled={isQuestionUnavailable(
                              rule,
                              condition,
                              question,
                            )}
                          >
                            {question.title || '名称未入力の設問'}
                          </option>
                        ))}
                      </select>
                    </label>
                    <label className="grid gap-1.5 text-xs font-semibold text-ink">
                      <span>選択肢</span>
                      <select
                        className={selectClassName}
                        aria-label={`組み合わせタグ ${ruleIndex + 1} 条件 ${conditionIndex + 1}の選択肢`}
                        value={condition.choiceClientId}
                        disabled={!selectedQuestion}
                        onChange={(event) => {
                          const choiceClientId = event.target.value
                          updateRule(rule.clientId, (current) => {
                            const currentCondition = current.conditions.find(
                              (candidate) =>
                                candidate.clientId === condition.clientId,
                            )
                            const currentQuestion = choiceQuestions.find(
                              (question) =>
                                question.clientId ===
                                currentCondition?.questionClientId,
                            )
                            if (
                              !currentCondition ||
                              (choiceClientId &&
                                (!currentQuestion ||
                                  isChoiceUnavailable(
                                    current,
                                    currentCondition,
                                    currentQuestion,
                                    choiceClientId,
                                  )))
                            ) {
                              return current
                            }
                            return {
                              ...current,
                              conditions: current.conditions.map((candidate) =>
                                candidate.clientId === condition.clientId
                                  ? { ...candidate, choiceClientId }
                                  : candidate,
                              ),
                            }
                          })
                        }}
                      >
                        <option value="">選択肢を選択</option>
                        {(selectedQuestion?.choices ?? []).map((choice) => {
                          const unavailable = isChoiceUnavailable(
                            rule,
                            condition,
                            selectedQuestion!,
                            choice.clientId,
                          )
                          return (
                            <option
                              key={choice.clientId}
                              value={choice.clientId}
                              disabled={unavailable}
                            >
                              {choice.label || '名称未入力の選択肢'}
                            </option>
                          )
                        })}
                      </select>
                    </label>
                    <Button
                      type="button"
                      size="icon"
                      variant="outline"
                      className="text-danger"
                      aria-label={`組み合わせタグ ${ruleIndex + 1} 条件 ${conditionIndex + 1}を削除`}
                      onClick={() =>
                        updateRule(rule.clientId, (current) => ({
                          ...current,
                          conditions: current.conditions.filter(
                            (currentCondition) =>
                              currentCondition.clientId !== condition.clientId,
                          ),
                        }))
                      }
                    >
                      <Icon icon="close" color="currentColor" width={16} height={16} />
                    </Button>
                  </div>
                )
              })}
            </div>

            <div className="flex flex-wrap items-center gap-3">
              <Button
                type="button"
                size="sm"
                variant="secondary"
                disabled={rule.conditions.length >= MAX_COMBO_CONDITIONS}
                onClick={() =>
                  updateRule(rule.clientId, (current) =>
                    current.conditions.length >= MAX_COMBO_CONDITIONS
                      ? current
                      : {
                          ...current,
                          conditions: [
                            ...current.conditions,
                            createEmptyComboCondition(),
                          ],
                        },
                  )
                }
              >
                <Icon icon="plus" color="currentColor" width={14} height={14} className="mr-1" />
                条件を追加
              </Button>
              {rule.conditions.length === MAX_COMBO_CONDITIONS ? (
                <p className="text-xs text-muted">
                  条件は最大{MAX_COMBO_CONDITIONS}件まで設定できます。
                </p>
              ) : null}
            </div>
          </div>

          <div className="grid gap-1.5">
            <span className="text-xs font-semibold text-ink">付与タグ</span>
            <p className="text-xs text-muted">
              条件をすべて満たした友だちに付けるタグを1つ選びます。
            </p>
            <SingleTagPicker
              tags={tags}
              value={rule.tagId}
              onChange={(tagId) =>
                updateRule(rule.clientId, (current) => ({
                  ...current,
                  tagId,
                }))
              }
              ariaLabel={`組み合わせタグ ${ruleIndex + 1}の付与タグ`}
              noneLabel="未選択"
            />
          </div>
        </div>
      ))}

      <div className="grid gap-2">
        <Button
          type="button"
          variant="secondary"
          className="justify-self-start"
          disabled={
            choiceQuestions.length === 0 || comboRules.length >= MAX_COMBO_RULES
          }
          onClick={() =>
            setComboRules((current) =>
              current.length >= MAX_COMBO_RULES
                ? current
                : [...current, createEmptyComboRule()],
            )
          }
        >
          <Icon icon="plus" color="currentColor" width={14} height={14} className="mr-1" />
          組み合わせタグを追加
        </Button>
        {comboRules.length === MAX_COMBO_RULES ? (
          <p className="text-xs text-muted">
            組み合わせタグは最大{MAX_COMBO_RULES}件まで設定できます。
          </p>
        ) : null}
        {comboRules.length > MAX_COMBO_RULES ? (
          <p
            className="rounded-xl border border-danger-border bg-danger-soft px-4 py-3 text-sm text-danger"
            role="alert"
          >
            組み合わせタグは最大{MAX_COMBO_RULES}件です。超過分を削除してください。
          </p>
        ) : null}
        {choiceQuestions.length === 0 ? (
          <p className="text-xs text-muted">
            単一選択または複数選択の設問を追加すると設定できます。
          </p>
        ) : null}
      </div>
    </SectionCard>
  )
}
